import clsx from 'clsx'
import { replace } from 'connected-react-router'
import moment from 'moment'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import type { InViewHookResponse } from 'react-intersection-observer'
import { useInView } from 'react-intersection-observer'
import { useLocation } from 'react-router'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import {
  fetchOfferFlightPrice,
  fetchRelatedTravelItems,
} from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import Breakpoint from 'components/Common/Breakpoint'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import ContentTagBanner from 'components/Content/ContentTagBanner'
import AccommodationOfferPageStickyFooter from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyFooter'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import SinglePropertyHotelFlightsToggleButton from 'components/OfferPage/Accommodation/HotelFlightsToggle/SinglePropertyHotelFlightsToggleButton'
import AlwaysOnMobileStickyHeaderFilters from 'components/OfferPage/AlwaysOn/MobileBookingFlow/AlwaysOnMobileStickyHeaderFilters'
import OfferAirportSelect from 'components/OfferPage/Filters/Airport/OfferAirportSelect'
import OfferAirportSelectBox from 'components/OfferPage/Filters/Airport/OfferAirportSelectBox'
import OfferCapacityWithAgesSelect from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCapacityWithAgesSelectBox from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferDatePickerSelect from 'components/OfferPage/Filters/DatePicker/OfferDatePickerSelect'
import OfferDatePickerSelectBox from 'components/OfferPage/Filters/DatePicker/OfferDatePickerSelectBox'
import OfferDurationTabs from 'components/OfferPage/Filters/DurationTabs/OfferDurationTabs'
import OfferPageFlightDealBanner from 'components/OfferPage/OfferPageFlightDealBanner'
import OfferPageGallery from 'components/OfferPage/OfferPageGallery'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import OfferYmalStickyFooter from 'components/Recommendations/YouMayAlsoLike/Offer/OfferYmalStickyFooter'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { OptimizelyExperiments } from 'constants/optimizely'
import { urlHashDatePicker } from 'constants/url'
import ModalProvider from 'contexts/ModalProvider'
import type {
  OfferPageFilterType,
  OfferPageRoom,
} from 'contexts/OfferPage/offerPageStateReducer'
import {
  OfferPageActions,
  initialOfferPageRooms,
} from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import GeoContext from 'contexts/geoContext'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useFlightPrice from 'hooks/useFlightPrice'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import usePropertyRates from 'hooks/usePropertyRates'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import useShareModalConfig from 'hooks/useShareModalConfig'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { countOccupantsForFlights, isValidFlightsOccupants } from 'lib/offer/occupancyUtils'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import { encodeSearchParams } from 'lib/search/searchUtils'
import {
  getCheckoutDate,
  getPropertyLoggedOutSavedPercent,
  getPropertyRatesKey,
  getShouldShowPropertyAnytimeRate,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import SavePromptHotel from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptHotel'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'

import PropertyIntro from './PropertyIntro'
import PropertyRoomOptions from './PropertyRoomOptions'
import PropertySidebarFilters from './PropertySidebarFilters'
import PropertyStickyFooter from './PropertyStickyFooter'
import PropertyStickyHeader from './PropertyStickyHeader'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import { mediaQueryDown } from 'components/utils/breakpoint'
import Caption from 'LuxKit/Typography/Caption'
import { verticalChildSpacing } from 'components/utils/spacing'

const RelativeBlock = styled.form`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;

  ${verticalChildSpacing[8]};
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const RoomOptionsWrapper = styled(LayoutContainer)`
  ${mediaQueryDown.mobile} {
    padding: 0;
  }
`

const FlexGroup = styled(Group)`
  flex-grow: 1;
`

const FlexDesktopFilterBlock = styled(DesktopFilterBlock)`
  flex-grow: 1;
`

interface Props {
  shouldUseLereStickyFooter: boolean
  lereYmalStickyFooterShowTrigger: InViewHookResponse
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer
  airport?: App.Airport
  duration: number
  activeFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  flights?: boolean
  areAnytimeRates: boolean
  shouldShowPropertyAnytimeRates: boolean
  availableForPurchase?: boolean
  loggedOutSavedPercent: number | undefined
  room?: App.PackageRoomType
  utmContent: string | undefined
}

const currentTimer = {} as Record<string, any>

const slideTimer = (name: string, callback: any, ms: number) => {
  if (currentTimer[name]) {
    clearTimeout(currentTimer[name])
  }
  currentTimer[name] = setTimeout(callback, ms)
}

function PropertyContent(props: Props) {
  const {
    shouldUseLereStickyFooter,
    lereYmalStickyFooterShowTrigger,
    property,
    offer,
    airport,
    duration,
    activeFilter,
    rooms,
    checkInDate,
    checkOutDate,
    flights,
    areAnytimeRates,
    shouldShowPropertyAnytimeRates,
    availableForPurchase,
    loggedOutSavedPercent,
    activeHeaderFilter,
    utmContent,
  } = props
  const destinationAirportCode = property.airportCode

  const intl = useIntl()
  const location = useLocation()
  const query = useQsQueryParams<{ roomRateId?: string, roomTypeId?: string, roomOptionRateId?: string }>()
  const { originAirports, airports } = useContext(GeoContext)
  const prevPath = useAppSelector(state => state.routeHistory.prevPath)

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()

  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.Occupants>>([])
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  const isMobileDevice = useScreenSizeOnly('mobile')
  // NB: This experiment is currently disabled, and should _not_ be re-enabled until we've rewritten
  // all the mobile flow components.
  const isNewMobileBookingFlowEnabled = useOptimizelyExperiment(OptimizelyExperiments.croAlwaysOnNewMobileBooking, isMobileDevice)
  const useNewMobileBookingFlow = isMobileDevice && isNewMobileBookingFlowEnabled

  const showHotelAndFlightsToggle = !offer.bundledWithFlightsOnly && property.areFlightsEnabled
  const showDesktopHotelAndFlightsToggle = !isMobileDevice && showHotelAndFlightsToggle

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
      flights,
    }).toString(), [checkInDate, checkOutDate, rooms, location.search, flights])

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, checkOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousRooms !== rooms ||
      previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  useIdleTimeReporter(IdleTimeSource.AlwaysOn)

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const hasRoomsChanged = useMemo(() => {
    const currentOccupancyNumber = rooms.map(generateOccupancyStringByRoom).join('|')
    const lastOccupancyNumber = (lastFetchedRooms.current ?? initialOfferPageRooms).map(generateOccupancyStringByRoom).join('|')
    return currentOccupancyNumber !== lastOccupancyNumber
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, activeFilter])

  useEffect(() => {
    const hasRooms = hasSelectedRooms(rooms)
    if (previousFilter === 'capacity' && !hasRooms) {
      if (!activeFilter) {
        autoScrollToFilters()
        scrollAfterFilterClose.current = false
      }
    }

    if (activeFilter) {
      autoScrollToFilters()
    }
  }, [activeFilter, activeHeaderFilter, previousFilter, hasRoomsChanged]) // eslint-disable-line react-hooks/exhaustive-deps

  const roomOption = useMemo(() => {
    if (!query.roomTypeId) return
    // Typically, `roomRateId` is for `gha-lpc` and `roomOptionRateId` is for `gha-lpp`.
    const roomRateId = query.roomRateId || query.roomOptionRateId
    if (!roomRateId) return
    return { roomTypeId: query.roomTypeId, roomRateId }
  }, [query.roomTypeId, query.roomRateId, query.roomOptionRateId])
  useEffect(() => {
    stateDispatch({ type: OfferPageActions.setRoomOption, roomOption })
  }, [stateDispatch, roomOption])

  useEffect(() => {
    slideTimer('fetchRelatedTravelItems', () => {
      dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
    }, 2000)
  }, [offer, duration, checkInDate, dispatch, rooms, checkOutDate])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useMountedEffect(() => {
    // effectively an 'on airport change' event
    if (airport) {
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(offer.id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (location.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...location, search: queryString }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  // Ensure property rates are loaded.
  usePropertyRates({
    offerType: OFFER_TYPE_ALWAYS_ON,
    offerId: offer.id,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    occupancies: rooms,
    utmContent,
    roomTypeId: roomOption?.roomTypeId,
    roomRateId: roomOption?.roomRateId,
  })
  // Ensure cheapest flights are also loaded. Note that (for now) we'll assume: (i) both LPC and
  // bedbank offers are configured identically (i.e. both have flights enabled and the same
  // destination airport code); and (ii) there's no `forceBundleId` for LPC offers (or its discount
  // is immaterial).
  useFlightPrice(
    destinationAirportCode,
    rooms,
    checkInDate,
    checkOutDate,
    airport?.code,
    duration,
  )

  const occupancyLimits = useMemo(() => {
    return Object.values(property.rooms).flatMap(({ capacities }) => capacities)
  }, [property.rooms])

  const isValidFlightsOccupancy = useMemo(() => {
    const flightOccupants = countOccupantsForFlights(rooms)
    return isValidFlightsOccupants(flightOccupants)
  }, [rooms])
  // TODO(HP-1495): Plumb flag through accommodation service property content endpoint.
  const shouldShowFlights = offer.bundledWithFlightsOnly || isValidFlightsOccupancy
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === destinationAirportCode), [airports, destinationAirportCode])
  const showFlightInformation = destinationAirport &&
    (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) &&
    shouldShowFlights

  const onBeforeDurationClick = useCallback((duration: number) => {
    if (!(areAnytimeRates && shouldShowPropertyAnytimeRates)) return true
    if (duration !== property.marketedLos) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      return false
    }
    return true
  }, [areAnytimeRates, property.marketedLos, shouldShowPropertyAnytimeRates, stateDispatch])

  // ------------- LERE sticky footer -------------------
  const recommendationFilters = useMemo(() => ({
    checkIn: checkInDate,
    checkOut: checkInDate ? moment(checkInDate).clone().add(duration, 'days').format(ISO_DATE_FORMAT) : undefined,
    rooms,
  }), [checkInDate, duration, rooms])

  const lereYmalStickyFooterHideTrigger = useInView({ triggerOnce: false })

  const canShowYmalFooter = lereYmalStickyFooterShowTrigger.inView && !lereYmalStickyFooterHideTrigger.inView
  const showDealBanner = airport && destinationAirport

  // ------------- END of LERE sticky footer -------------------

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  const shareConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', '),
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType: 'hotel',
  })

  // Do not show the Expired recommendation when it comes from my escapes
  const shouldShowLere = useMemo(() => hasOfferExpired(offer) && !prevPath.includes('/my-escapes/order'), [offer, prevPath])

  return <ProductPaletteProvider product={offer}>
    <ImmersiveSaveButtonPortal>
      <BookmarkButton offer={offer} />
    </ImmersiveSaveButtonPortal>
    <TripGuard>
      <SavePromptHotel offer={offer} />
    </TripGuard>
    <RelativeBlock onSubmit={(e) => e.preventDefault()}>
      {useNewMobileBookingFlow && <AccommodationOfferPageStickyHeaderMobile>
        <AlwaysOnMobileStickyHeaderFilters />
      </AccommodationOfferPageStickyHeaderMobile>}
      <div>
        <CSSBreakpoint min="tablet" dynamic>
          <PropertyStickyHeader
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />
        </CSSBreakpoint>
        {shouldShowLere && (
          <div>
            <ExpiredDealsLereRecommendations hideOffers={!!activeFilter} />
          </div>
        )}
        {availableForPurchase && (
          <WalledContent enforced={offer.walledGarden}>
            <Breakpoint max="mobile">
              <ModalProvider>
                <PropertySidebarFilters
                  onViewPackageOptions={scrollToPackageOptions}
                  capacities={occupancyLimits}
                  durationOptionsOverride={property.durationOptions}
                  shouldShowCalendarWithoutPricing
                />
              </ModalProvider>
            </Breakpoint>
          </WalledContent>
        )}
        <span ref={lereYmalStickyFooterHideTrigger.ref} />
        <OfferPageGallery offer={offer} shareModalConfig={shareConfig} autoPlayVideo={!!offer.isVideoVimeoHero} />
        <Pane>
          <PropertyIntro
            property={property}
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
            shareConfig={shareConfig}
          />
        </Pane>
      </div>
      <ScrollAnchor ref={packageOptionsAnchor} />
      <SoftOfferPageWalledGarden>
        <span ref={lereYmalStickyFooterShowTrigger.ref} />
        <PackageOptionsPane id="Package-Options">
          <LayoutContainer>
            <VerticalSpacer gap={32}>
              {availableForPurchase && (
                <WalledContent enforced={offer.walledGarden}>
                  <>
                    {!useNewMobileBookingFlow && (
                      <CSSBreakpoint max="mobile" dynamic>
                        <Group direction="vertical" gap={16}>
                          {/* We've temporarily disabled `cro_lpc_new_mobile_booking`, so we're on
                          the old mobile flow. However, we're not showing hotel-only and
                          hotel-with-flights pricing side-by-side, so we need this toggle to be
                          present. */}
                          <SinglePropertyHotelFlightsToggleButton />
                          <Group direction="vertical" gap={8}>
                            <OfferCapacityWithAgesSelectBox />
                            <OfferDatePickerSelectBox />
                            {showFlightInformation && flights && <OfferAirportSelectBox />}
                          </Group>
                        </Group>
                      </CSSBreakpoint>
                    )}
                    <CSSBreakpoint min="tablet" dynamic>
                      <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
                      {showDesktopHotelAndFlightsToggle && (
                        <Group ref={desktopFiltersWrapperRef} fullWidth direction="horizontal" gap={8} horizontalAlign="space-between" wrap="wrap">
                          <FlexGroup direction="horizontal" horizontalAlign="center" desktopHorizontalAlign="start">
                            <SinglePropertyHotelFlightsToggleButton />
                          </FlexGroup>
                          <FlexDesktopFilterBlock direction="horizontal" gap={8} horizontalAlign="center" desktopHorizontalAlign="end" wrap="wrap" className={clsx({ open: !!activeFilter })}>
                            {showFlightInformation && flights && <OfferAirportSelect />}
                            <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />
                            <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />
                            <Backdrop visible={!!activeFilter && !isMobileDevice} data-testid="filter-backdrop" />
                          </FlexDesktopFilterBlock>
                        </Group>
                      )}
                      {!showDesktopHotelAndFlightsToggle && (
                        <DesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" className={clsx({ open: !!activeFilter })}>
                          <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />
                          <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />
                          {showFlightInformation && <OfferAirportSelect />}
                          <Backdrop visible={!!activeFilter && !isMobileDevice} data-testid="filter-backdrop" />
                        </DesktopFilterBlock>
                      )}
                    </CSSBreakpoint>
                  </>
                </WalledContent>
              )}

              {!useNewMobileBookingFlow && (
                <VerticalSpacer gap={8}>
                  <BodyText variant="large" align="center">
                    {intl.formatMessage({
                      id: 'J45fSGD5me',
                      defaultMessage: 'Select duration',
                      description: 'Text label for duration selection section on property page',
                    })}
                  </BodyText>
                  <OfferDurationTabs
                    durationOptionsOverride={property.durationOptions}
                    onBeforeDurationClick={onBeforeDurationClick}
                    availableForPurchaseOverride={availableForPurchase}
                  />
                </VerticalSpacer>
              )}

              <VerticalSpacer gap={16}>
                <ContentTagBanner offerId={offer.id} page="offer" />
                {showDealBanner && config.FLIGHT_ENABLED && !isAgentHubEnabled && !checkInDate && <OfferPageFlightDealBanner
                  destinationAirportCode={destinationAirport.code}
                  originAirportCode={airport.code}
                />}
              </VerticalSpacer>
            </VerticalSpacer>
          </LayoutContainer>
          <RoomOptionsWrapper>
            {offer.packages.length > 0 && !useNewMobileBookingFlow && <PropertyRoomOptions
              onViewPackageOptions={scrollToPackageOptions}
            />}
          </RoomOptionsWrapper>
          {showFlightInformation && <LayoutContainer>
            <Caption variant="medium" align="center">
              {intl.formatMessage(
                {
                  id: 'iEpQbGKbrK',
                  defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply.',
                  description: 'Flight pricing disclaimer text',
                },
                { brandName: config.brandConfig.name },
              )}
            </Caption>
          </LayoutContainer>}
        </PackageOptionsPane>
        {!useNewMobileBookingFlow && availableForPurchase && (!shouldUseLereStickyFooter || !canShowYmalFooter) &&
          <PropertyStickyFooter
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />}
        {useNewMobileBookingFlow && availableForPurchase && <AccommodationOfferPageStickyFooter />}
      </SoftOfferPageWalledGarden>
    </RelativeBlock>
    <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    {shouldUseLereStickyFooter && !useNewMobileBookingFlow && (
      <ErrorBoundary fallback={null}>
        <OfferYmalStickyFooter
          seedOffer={offer}
          filters={recommendationFilters}
          show={canShowYmalFooter}
          initialShowDelayMs={2000}
          hideImmediately={!!activeFilter}
          pageSource="AlwaysOn"
        />
      </ErrorBoundary>
    )}
  </ProductPaletteProvider>
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'flights'
  | 'room'
  | 'areAnytimeRates'
  | 'shouldShowPropertyAnytimeRates'
  | 'availableForPurchase'
  | 'loggedOutSavedPercent'
  | 'activeHeaderFilter'
  | 'utmContent'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  flights: state.flights,
  room: state.room,
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  shouldShowPropertyAnytimeRates: getShouldShowPropertyAnytimeRate(state, appState),
  availableForPurchase: isPropertyAvailableForPurchase(state, appState),
  loggedOutSavedPercent: getPropertyLoggedOutSavedPercent(state, appState),
  activeHeaderFilter: state.activeHeaderFilter,
  utmContent: appState.router.location.query.utm_content,
}), PropertyContent)
